import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { isValidCompanyUser } from '../config/msalConfig';
import { setTokenAcquirer, setOnAuthFailure } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Register a token acquirer with the api module so every request
  // gets a fresh token via MSAL's acquireTokenSilent. MSAL handles
  // caching internally — if the cached token is still valid it returns
  // instantly, otherwise it silently refreshes using its own refresh token.
  useEffect(() => {
    const acquireToken = async () => {
      if (accounts.length > 0) {
        const response = await instance.acquireTokenSilent({
          scopes: ["openid", "profile", "email"],
          account: accounts[0]
        });
        return response.idToken;
      }
      return null;
    };

    setTokenAcquirer(acquireToken);
  }, [instance, accounts]);

  // When the api module receives a 401, clear the user state.
  // ProtectedRoute will see isAuthenticated become false and
  // redirect to /login via React Router — no hard page reload.
  useEffect(() => {
    setOnAuthFailure(() => {
      setUser(null);
    });
  }, []);

  // Check authentication status when MSAL finishes initialising
  // or when the account list changes (login/logout).
  useEffect(() => {
    checkAuthStatus();
  }, [accounts, inProgress]);

  const checkAuthStatus = async () => {
    try {
      if (inProgress === "none" && accounts.length > 0) {
        const account = accounts[0];

        // Validate company domain
        if (!isValidCompanyUser(account.username)) {
          setError('Access denied. Only company accounts are allowed.');
          await instance.logoutRedirect();
          setLoading(false);
          return;
        }

        // Verify we can actually acquire a token for this account
        try {
          await instance.acquireTokenSilent({
            scopes: ["openid", "profile", "email"],
            account: account
          });

          setUser({
            id: account.homeAccountId,
            username: account.username,
            name: account.name,
            email: account.username,
            provider: 'microsoft'
          });
        } catch (tokenError) {
          // Silent acquisition failed — don't auto-redirect, let user
          // click "Sign in" again.
          setUser(null);
        }
      } else if (inProgress === "none" && accounts.length === 0) {
        // No active session
        setUser(null);
      }
    } catch (error) {
      setError('Authentication failed');
    } finally {
      if (inProgress === "none") {
        setLoading(false);
      }
    }
  };

  const login = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const response = await instance.loginRedirect({
        scopes: ["openid", "profile", "email"]
      });
      return { success: true };
    } catch (error) {
      const errorMessage = error.message || 'Microsoft login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      setUser(null);
      setError(null);
      await instance.logoutRedirect();
    } catch (error) {
      // Logout redirect failed — local state is already cleared
      setUser(null);
      setError(null);
    }
  }, [instance]);

  const value = useMemo(() => ({
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
  }), [user, loading, error, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
